import { createContext, useContext, useEffect, useMemo, useState, type PropsWithChildren } from "react";
import { ApiError, apiRequest, buildPublicUrl } from "../lib/api";
import { categories as fallbackCategories, dishes as fallbackDishes } from "../data/siteData";
import type { MenuCatalog, MenuCategory, MenuDish } from "../types/menu";

const MENU_REFRESH_INTERVAL_MS = 60_000;

function normalizeImageUrl(image?: string) {
  if (!image) {
    return undefined;
  }

  return buildPublicUrl(image);
}

function normalizeCatalog(nextCatalog: MenuCatalog): MenuCatalog {
  return {
    categories: [...nextCatalog.categories]
      .map((category) => ({
        ...category,
        image: normalizeImageUrl(category.image)
      }))
      .sort((left, right) => left.sortOrder - right.sortOrder),
    dishes: [...nextCatalog.dishes]
      .map((dish) => ({
        ...dish,
        image: normalizeImageUrl(dish.image)
      }))
      .sort((left, right) => {
        if (left.category === right.category) {
          return (left.sortOrder ?? 0) - (right.sortOrder ?? 0);
        }

        return left.category.localeCompare(right.category, "ru");
      }),
    updatedAt: nextCatalog.updatedAt
  };
}

function buildFallbackCatalog(): MenuCatalog {
  const categories = fallbackCategories
    .filter((category) => category.id !== "all")
    .map((category, index) => {
      const categoryDishes = fallbackDishes.filter((dish) => dish.category === category.id);
      const cover = categoryDishes.find((dish) => dish.image)?.image;
      const accent = categoryDishes[0]?.tone ?? "jade";

      return {
        id: category.id,
        label: category.label,
        description:
          category.id === "hot"
            ? "Плотные блюда для основного стола, банкета и домашнего ужина."
            : category.id === "soup"
              ? "Спокойные суповые позиции на каждый день."
              : "Быстрый формат с гибкой настройкой ингредиентов и добавок.",
        accent,
        image: normalizeImageUrl(cover),
        sortOrder: index,
        dishCount: categoryDishes.length,
        isVisible: true
      } satisfies MenuCategory;
    });

  const dishes = fallbackDishes.map((dish, index) => ({
    ...dish,
    image: normalizeImageUrl(dish.image),
    available: true,
    sortOrder: index,
    customization: dish.customization
      ? {
          removableIngredients: dish.customization.removableIngredients,
          addOns: dish.customization.addOns
        }
      : undefined
  })) satisfies MenuDish[];

  return {
    categories,
    dishes,
    updatedAt: new Date().toISOString()
  };
}

interface MenuContextValue {
  isLoading: boolean;
  catalog: MenuCatalog;
  categories: MenuCategory[];
  dishes: MenuDish[];
  featuredDishes: MenuDish[];
  getDishById: (dishId: string) => MenuDish | undefined;
  getDishesByCategory: (categoryId: string) => MenuDish[];
  refresh: () => Promise<void>;
}

const fallbackCatalog = buildFallbackCatalog();
const MenuContext = createContext<MenuContextValue | null>(null);

export function MenuProvider({ children }: PropsWithChildren) {
  const [isLoading, setIsLoading] = useState(true);
  const [catalog, setCatalog] = useState<MenuCatalog>(fallbackCatalog);

  const refresh = async () => {
    try {
      const nextCatalog = await apiRequest<MenuCatalog>("/api/menu");
      setCatalog(normalizeCatalog(nextCatalog));
    } catch (error) {
      if (!(error instanceof ApiError)) {
        console.error(error);
      }
    }
  };

  useEffect(() => {
    let mounted = true;

    const bootstrap = async () => {
      setIsLoading(true);

      try {
        const nextCatalog = await apiRequest<MenuCatalog>("/api/menu");

        if (!mounted) {
          return;
        }

        setCatalog(normalizeCatalog(nextCatalog));
      } catch (error) {
        if (!mounted) {
          return;
        }

        if (!(error instanceof ApiError)) {
          console.error(error);
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    void bootstrap();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      void refresh();
    }, MENU_REFRESH_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  const value = useMemo<MenuContextValue>(() => {
    const dishesById = new Map(catalog.dishes.map((dish) => [dish.id, dish]));
    const featuredDishes = catalog.dishes.filter((dish) => dish.featured);

    return {
      isLoading,
      catalog,
      categories: catalog.categories,
      dishes: catalog.dishes,
      featuredDishes,
      getDishById: (dishId) => dishesById.get(dishId),
      getDishesByCategory: (categoryId) =>
        catalog.dishes.filter((dish) => dish.category === categoryId && dish.available !== false),
      refresh
    };
  }, [catalog, isLoading]);

  return <MenuContext.Provider value={value}>{children}</MenuContext.Provider>;
}

export function useMenu() {
  const context = useContext(MenuContext);

  if (!context) {
    throw new Error("useMenu must be used within MenuProvider");
  }

  return context;
}
